import { DateTime } from "luxon";
import {
  AdmissionDisplayMode,
  DateRange,
} from "src/app/core/models/admissions.model";
import { SelectableEnumerationItem } from "src/app/core/models/lookup.model";

const STORAGE_KEY_PREFIX = "wa-referrals-filters-session";

export type ReferralsFiltersSessionSnapshot = {
  dateStartIso: string;
  dateEndIso: string;
  displayModeView: number;
  displayModeFilter: number;
  scheduleStatusFiltersTouched: boolean;
  statusValues: string[];
  authStatusValues: string[];
  physicianIds: string[];
  resourceIds: string[];
  locationValues: string[];
  visitTypeValues: string[];
  referralSourceValues: string[];
};

type SessionUserScope = {
  id?: string;
  companyId?: string;
} | null | undefined;

function sessionStorageKey(user: SessionUserScope): string | null {
  const userId = String(user?.id ?? "").trim();
  if (!userId) {
    return null;
  }
  const companyId = String(user?.companyId ?? "").trim() || "default";
  const safe = (value: string) => value.replace(/[^a-zA-Z0-9_-]/g, "_");
  return `${STORAGE_KEY_PREFIX}:${safe(userId)}_${safe(companyId)}`;
}

export function saveReferralsFiltersSession(
  user: SessionUserScope,
  snapshot: ReferralsFiltersSessionSnapshot
): void {
  const key = sessionStorageKey(user);
  if (!key) {
    return;
  }
  try {
    sessionStorage.setItem(key, JSON.stringify(snapshot));
  } catch {
    // ignore quota / private mode
  }
}

export function loadReferralsFiltersSession(
  user: SessionUserScope
): ReferralsFiltersSessionSnapshot | null {
  const key = sessionStorageKey(user);
  if (!key) {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<ReferralsFiltersSessionSnapshot>;
    if (
      !parsed.dateStartIso ||
      !parsed.dateEndIso ||
      !DateTime.fromISO(parsed.dateStartIso).isValid ||
      !DateTime.fromISO(parsed.dateEndIso).isValid
    ) {
      return null;
    }
    return {
      dateStartIso: parsed.dateStartIso,
      dateEndIso: parsed.dateEndIso,
      displayModeView: Number(parsed.displayModeView ?? 1),
      displayModeFilter: Number(parsed.displayModeFilter ?? 0),
      scheduleStatusFiltersTouched: !!parsed.scheduleStatusFiltersTouched,
      statusValues: normalizeStringArray(parsed.statusValues),
      authStatusValues: normalizeStringArray(parsed.authStatusValues),
      physicianIds: normalizeStringArray(parsed.physicianIds),
      resourceIds: normalizeStringArray(parsed.resourceIds),
      locationValues: normalizeStringArray(parsed.locationValues),
      visitTypeValues: normalizeStringArray(parsed.visitTypeValues),
      referralSourceValues: normalizeStringArray(parsed.referralSourceValues),
    };
  } catch {
    return null;
  }
}

export function clearReferralsFiltersSession(user: SessionUserScope): void {
  const key = sessionStorageKey(user);
  if (!key) {
    return;
  }
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export function buildReferralsFiltersSessionSnapshot(input: {
  selectedDate: DateRange;
  displayMode: AdmissionDisplayMode;
  scheduleStatusFiltersTouched: boolean;
  statusList: SelectableEnumerationItem[];
  authStatusList: SelectableEnumerationItem[];
  physicianList: SelectableEnumerationItem[];
  resourceList: SelectableEnumerationItem[];
  locationList: SelectableEnumerationItem[];
  visitTypeList: SelectableEnumerationItem[];
  referralSourceList: SelectableEnumerationItem[];
}): ReferralsFiltersSessionSnapshot {
  const selectedValues = (list: SelectableEnumerationItem[]) =>
    list
      .filter((item) => item.isSelected)
      .map((item) => String(item.value ?? "").trim())
      .filter(Boolean);

  return {
    dateStartIso: DateTime.fromJSDate(input.selectedDate.startDate).toISO() ?? "",
    dateEndIso: DateTime.fromJSDate(input.selectedDate.endDate).toISO() ?? "",
    displayModeView: input.displayMode.view,
    displayModeFilter: input.displayMode.filter,
    scheduleStatusFiltersTouched: input.scheduleStatusFiltersTouched,
    statusValues: selectedValues(input.statusList),
    authStatusValues: selectedValues(input.authStatusList),
    physicianIds: selectedValues(input.physicianList),
    resourceIds: selectedValues(input.resourceList),
    locationValues: selectedValues(input.locationList),
    visitTypeValues: selectedValues(input.visitTypeList).map((v) =>
      v.toLowerCase()
    ),
    referralSourceValues: selectedValues(input.referralSourceList),
  };
}

function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item) => String(item ?? "").trim())
    .filter(Boolean);
}

export function applySelectedValuesToFilterList(
  list: SelectableEnumerationItem[],
  selectedValues: string[],
  options?: { compareLowercase?: boolean }
): void {
  const want = new Set(
    selectedValues
      .map((value) =>
        options?.compareLowercase
          ? String(value ?? "").trim().toLowerCase()
          : String(value ?? "").trim()
      )
      .filter(Boolean)
  );
  for (const item of list) {
    const raw = String(item.value ?? "").trim();
    const key = options?.compareLowercase ? raw.toLowerCase() : raw;
    item.isSelected = !!key && want.has(key);
  }
}

export function buildPlaceholderSelectedFilters(
  values: string[],
  compareLowercase = false
): SelectableEnumerationItem[] {
  return values.map((value) => ({
    displayName: value,
    value: compareLowercase ? value.toLowerCase() : value,
    isSelected: true,
  }));
}
